import { useEffect, useState } from 'react';
import { API_URL, buildKioskAuthHeaders } from './api';
import './ProductOffers.css';

const SHOP_ORIGINS = {
  becaisse: 'https://becaisse.fr',
  bekiosk: 'https://beborne.com',
  beresto: 'https://beresto.fr',
};
const hasProduct = product => Object.prototype.hasOwnProperty.call(SHOP_ORIGINS, product);
const isPrice = value => Number.isSafeInteger(value) && value >= 0;
function validOfferUrl(value, product, id) {
  try {
    const url = new URL(value);
    return url.origin === SHOP_ORIGINS[product] && !url.username && !url.password
      && url.pathname === '/boutique.html' && url.searchParams.get('item') === id
      && [...url.searchParams].length === 1 && url.hash === `#product-${id}`;
  } catch (_) { return false; }
}
function validCatalog(data, product) {
  return data?.schemaVersion === 1 && data.product === product && data.currency === 'EUR'
    && data.vatRate === 20 && [true, false, null].includes(data.paymentConfigured)
    && Array.isArray(data.offers) && new Set(data.offers.map(offer => offer?.id)).size === data.offers.length
    && data.offers.every(offer => offer && typeof offer.id === 'string' && /^[a-z0-9_-]+$/.test(offer.id)
      && typeof offer.name === 'string' && offer.name.trim() && typeof offer.description === 'string'
      && typeof offer.unit === 'string' && offer.unit.trim() && isPrice(offer.monthlyHtCents)
      && (offer.annualHtCents == null || isPrice(offer.annualHtCents))
      && (offer.additionalAccountMonthlyHtCents == null || isPrice(offer.additionalAccountMonthlyHtCents))
      && typeof offer.monthlyOnly === 'boolean' && typeof offer.purchaseAvailable === 'boolean'
      && validOfferUrl(offer.url, product, offer.id));
}
const euro = cents => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
}).format(cents / 100);

export default function ProductOffers({ product }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', product });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading', product });
    async function load() {
      try {
        if (!hasProduct(product)) throw new Error('invalid-product');
        const response = await fetch(`${API_URL}/api/admin/options/offers?product=${product}`, {
          method: 'GET', headers: buildKioskAuthHeaders(), signal: controller.signal,
        });
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (!validCatalog(data, product)) throw new Error('invalid-catalog');
        if (active) setState({ status: 'loaded', product, data });
      } catch (_) {
        if (active) setState({ status: 'error', product });
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [product, attempt]);
  const current = state.product === product ? state : { status: 'loading' };
  return <section className="product-offers" aria-labelledby="product-offers-title">
    <div className="product-offers-heading"><span>Développer votre activité</span><h2 id="product-offers-title">Offres et compléments</h2>
      <p>Découvrez les offres de votre boutique. Leur présentation ne modifie pas les accès de votre établissement.</p></div>
    {current.status === 'loading' && <p role="status">Chargement des offres…</p>}
    {current.status === 'error' && <div className="product-offers-message" role="alert">
      <p>Les offres sont momentanément indisponibles. Vos accès restent consultables ci-dessus.</p>
      <button type="button" onClick={() => setAttempt(value => value + 1)}>Réessayer les offres</button>
    </div>}
    {current.status === 'loaded' && <>
      {current.data.offers.length === 0 && <p>Aucune offre à afficher pour cette boutique.</p>}
      {current.data.paymentConfigured !== true && current.data.offers.some(offer => offer.purchaseAvailable) && <p className="product-offers-note">La disponibilité du paiement est à confirmer en boutique.</p>}
      <div className="product-offers-grid">{current.data.offers.map(offer => <article className="product-offer" key={offer.id} aria-label={`Offre ${offer.name}`}>
        <span className="product-offer-badge">{offer.purchaseAvailable ? 'Offre en boutique' : 'Sur devis'}</span>
        <h3>{offer.name}</h3>
        {offer.description && <p className="product-offer-description">{offer.description}</p>}
        <div className="product-offer-pricing"><p><strong>{euro(offer.monthlyHtCents)}</strong> HT / mois / {offer.unit}</p>
          {offer.additionalAccountMonthlyHtCents != null && <p className="product-offer-additional">Puis {euro(offer.additionalAccountMonthlyHtCents)} HT/mois par établissement supplémentaire</p>}
          {!offer.monthlyOnly && offer.annualHtCents != null && <p className="product-offer-annual">{euro(offer.annualHtCents)} HT / an / {offer.unit}</p>}
        </div>
        <a href={offer.url} target="_blank" rel="noopener noreferrer">Voir l’offre <span aria-hidden="true">↗</span><span className="product-offers-sr-only"> {offer.name} (nouvel onglet)</span></a>
      </article>)}</div>
      {current.data.offers.length > 0 && <p className="product-offers-note">Tarifs hors taxes. Conditions, disponibilité et éventuelle activation à confirmer dans la boutique.</p>}
    </>}
  </section>;
}
